import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="vin-lookup"
// Auto-fills vehicle details when a VIN is entered (with debounce)
//
// Usage:
//   <form data-controller="vin-lookup" data-vin-lookup-url-value="/api/vehicles">
//     <input data-vin-lookup-target="vin"
//            data-action="input->vin-lookup#lookup paste->vin-lookup#onPaste"
//            name="vehicle[vin]">
//     <input data-vin-lookup-target="year" name="vehicle[year]">
//     <input data-vin-lookup-target="make" name="vehicle[make]">
//     <input data-vin-lookup-target="model" name="vehicle[model]">
//   </form>
//
export default class extends Controller {
  static targets = ["vin", "year", "make", "model", "mileage", "engineSize", "transmissionType", "vehicleType", "spinner", "message"]

  static values = {
    url: String,
    debounce: { type: Number, default: 500 }
  }

  // VINs never contain I, O, or Q
  static INVALID_VIN_CHARS = /[IOQ]/i

  connect() {
    this.debounceTimer = null
    this.abortController = null
  }

  disconnect() {
    this.cancelPendingRequest()
  }

  onPaste(event) {
    event.preventDefault()
    const pasted = (event.clipboardData || window.clipboardData).getData("text")
    const cleaned = pasted.toUpperCase().replace(/[^A-Z0-9]/g, "")
    this.vinTarget.value = cleaned
    this.vinTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  lookup() {
    const raw = this.vinTarget.value.trim().toUpperCase().replace(/[^A-Z0-9]/g, "")
    const length = raw.length

    // Clear any pending request
    this.cancelPendingRequest()
    this.hideMessage()

    // Show character counter while typing
    if (length > 0 && length < 17) {
      this.showCounter(length)
      return
    }

    // Nothing typed
    if (length === 0) {
      this.hideSpinner()
      return
    }

    // Check for invalid VIN characters (I, O, Q)
    if (this.constructor.INVALID_VIN_CHARS.test(raw)) {
      this.hideSpinner()
      this.showMessages(["VINs cannot contain I, O, or Q"], "error")
      return
    }

    // Exactly 17 characters — trigger lookup
    this.hideSpinner()
    this.debounceTimer = setTimeout(() => {
      this.fetchVehicleDetails(raw)
    }, this.debounceValue)
  }

  async fetchVehicleDetails(vin) {
    if (!this.hasUrlValue) return

    this.showLoading()

    try {
      this.abortController = new AbortController()

      const url = `${this.urlValue}/${encodeURIComponent(vin)}`

      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        signal: this.abortController.signal
      })

      if (!response.ok) throw new Error("Network response was not ok")

      const data = await response.json()

      this.hideSpinner()

      if (data.found) {
        this.fillFields(data)
        if (data.messages && data.messages.length > 0) {
          this.showMessages(data.messages, "warning")
        }
      } else if (data.messages && data.messages.length > 0) {
        this.showMessages(data.messages, "error")
      } else {
        this.showNotFound()
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("VIN lookup failed:", error)
        this.hideSpinner()
      }
    } finally {
      this.abortController = null
    }
  }

  fillFields(data) {
    if (data.year) this.setFieldValue('year', data.year)
    if (data.make) this.setFieldValue('make', data.make)
    if (data.model) this.setFieldValue('model', data.model)
    if (data.engine_size) this.setFieldValue('engineSize', data.engine_size)
    if (data.transmission_type) this.setFieldValue('transmissionType', data.transmission_type)
    if (data.vehicle_type) this.setFieldValue('vehicleType', data.vehicle_type)

    // Only populate mileage if the field is empty (don't overwrite user input)
    if (data.last_mileage && this.hasMileageTarget && !this.mileageTarget.value) {
      this.setFieldValue('mileage', data.last_mileage)
    }
  }

  // Helper to set a field value, handling both regular inputs and searchable-select components
  setFieldValue(targetName, value) {
    const capitalizedName = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (!this[`has${capitalizedName}Target`]) return

    const target = this[`${targetName}Target`]

    // Check if target contains or is inside a searchable-select controller
    const ssElement = target.querySelector?.('[data-controller~="searchable-select"]')
      || target.closest?.('[data-controller~="searchable-select"]')
    if (ssElement) {
      const ssController = this.application.getControllerForElementAndIdentifier(ssElement, 'searchable-select')
      if (ssController) {
        ssController.setValueExternally(value, value)
        return
      }
    }

    // Regular input/select
    target.value = value
  }

  cancelPendingRequest() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }

  // --- Spinner (inside input) ---

  showLoading() {
    if (!this.hasSpinnerTarget) return
    this.spinnerTarget.innerHTML = '<span class="loading loading-spinner loading-sm"></span>'
    this.spinnerTarget.classList.remove("hidden")
  }

  showCounter(length) {
    if (!this.hasSpinnerTarget) return
    this.spinnerTarget.textContent = `${length}/17`
    this.spinnerTarget.className = "absolute right-3 top-1/2 -translate-y-1/2 text-base-content/50 text-xs"
  }

  hideSpinner() {
    if (!this.hasSpinnerTarget) return
    this.spinnerTarget.classList.add("hidden")
  }

  // --- Messages (below input) ---

  showNotFound() {
    if (!this.hasMessageTarget) return
    const span = document.createElement("span")
    span.textContent = "New vehicle"
    span.className = "text-secondary text-xs"
    this.messageTarget.replaceChildren(span)
    this.messageTarget.classList.remove("hidden")
    setTimeout(() => this.hideMessage(), 3000)
  }

  showMessages(messages, level) {
    if (!this.hasMessageTarget) return
    const colorClass = level === "warning" ? "text-warning-content" : "text-error"
    const container = document.createElement("div")
    container.className = "flex flex-col gap-1"
    messages.forEach(m => {
      const div = document.createElement("div")
      div.textContent = m
      div.className = `${colorClass} text-xs`
      container.appendChild(div)
    })
    this.messageTarget.replaceChildren(container)
    this.messageTarget.classList.remove("hidden")
  }

  hideMessage() {
    if (!this.hasMessageTarget) return
    this.messageTarget.classList.add("hidden")
  }
}
